import "./approval-requests.scss";

import * as React from "react";

import {faArrowDown, faArrowUp, faCircleXmark, faSearch} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}         from "@fortawesome/react-fontawesome";
import {cx}                      from "emotion";

import {cs}                             from "@Common/react/chain-services";
import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                                       from "@Common/react/cst2";
import {Invoke}                         from "@Common/react/invoke";
import {Load}                           from "@Common/react/load";
import {Load2}                          from "@Common/react/load2";
import {StorageState}                   from "@Common/react/storage-state";
import {UseState}                       from "@Common/react/use-state";
import {Types}                          from "@Common/types";
import {BubbleNoti}                     from "@Common/ui-components/bubble-noti/bubble-noti";
import {SearchableSelect}               from "@Common/ui-components/searchable-select/searchable-select";
import {Select2}                        from "@Common/ui-components/select-2/select-2";
import {MEDIA_QUERY_MD, ViewportTest}   from "@Common/ui-components/viewport-test";
import {buildUrlQuery}                  from "@Common/utils/build-url-query";
import {cascadeMap}                     from "@Common/utils/cascade";
import {sortMulti, sort as sorted}      from "@Common/utils/collections";
import {chain}                          from "@Common/utils/fs";
import {values}                         from "@Common/utils/objects";

import {consumeContext}      from "@Xpresso/loaders/consume-context";
import {OrderDetailsSidebar} from "@Xpresso/routes/account/orders/common/order-details/order-details-sidebar";
import {
    BRANCH_LEVEL,
    COMPANY_LEVEL
}                            from "@Xpresso/routes/common/logic/user-level";
import {
    ATTRIBUTE_KEY_LABELS,
    SHIPPING_SERVICE_LEVELS_SORTED,
    ApprovalRequestAttribute,
    ApprovalRequestAttributesDict,
    ApprovalRequestAttributesService,
    isApprovalAttributeOfKey,
}                                     from "./approval-request-attributes";
import {ApprovalRequestsCardListView} from "./card-list-view/card-list-view";
import {ApprovalRequestsTableView}    from "./table-view/table-view";


export type ApprovalRequest = {
    approvalType     : Types.Apis.Payloads.Next.OrderApproval.ApprovalType,
    approverLevel    : Types.Apis.Models.UserLevel,
    id               : number,
    appliedCredits  ?: Types.Apis.Models.AppliedCredits,
    appliedPayments ?: Types.Apis.Models.AppliedPayments,
    shipping        ?: Types.Apis.Models.CartPendingApproval['appliedPayments']['shipping'],
} & (
    | {
        approvalGate   : "cart",
        orders          : Types.Apis.Models.OrderPendingApproval[],
    }
    | {
        approvalGate   : "order",
        orders          : Types.Apis.Models.StandaloneOrderPendingApproval[],
    }
    | {
        approvalGate   : "proof",
        orders          : Types.Apis.Models.ProofPendingApproval[],
    }
);

export type ApprovalRequestSortMode = {
    defaultAscending    : boolean,
    name                : string,
    label               : string,
    getSortValue        : (approvalEntry:{approval: ApprovalRequest, attributes: ApprovalRequestAttributesDict}, ascending?: boolean) => any
};

export const ApprovalRequests = () => (
    makeChain<JSX.Element>()
    .then(({}, next:ComponentLink<typeof ClearEmulation>) => ClearEmulation({next}))

    .then(...consumeContext("apis"))
    .then(...consumeContext("session"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("routerHistory"))
    .then("noti", ({}, next:ComponentLink<typeof BubbleNoti>) => BubbleNoti({next}))
    .then("orderDetailsSidebar", ({}, next:ComponentLink<typeof OrderDetailsSidebar>) => OrderDetailsSidebar({next}))
    .then("branchList", ({apis}, next) => Load({
        fetch: () => apis.next.getBranchList(),
        next,
    }))
    .then("selectedBranch", ({session, userLevels}, next:ComponentLink<typeof UseState<string>>) => UseState({
        initValue: userLevels.userLevel === BRANCH_LEVEL ? session.value?.branchId : null,
        next,
    }))
    .then("showOfficeApprovals", ({}, next:ComponentLink<typeof UseState<string>>) => UseState({
        initValue: null,
        next,
    }))
    .then("approvalRequestsLoad", ({apis, selectedBranch, showOfficeApprovals}, next:ComponentLink<typeof Load2<ApprovalRequest[]>>) => Load2({
        _key: JSON.stringify(selectedBranch.value) + ' ' + JSON.stringify(showOfficeApprovals.value),
        fetch: async () => {
            const apiResponse = await apis.next.approval.getApprovalRequests(selectedBranch.value, showOfficeApprovals.value);
            return [
                ...cascadeMap(apiResponse, ["*", "cartLevelApproval", "*"], ({
                    appliedPayments: {appliedCredits, appliedPayments, shipping},
                    finalApprovalLevel: approverLevel, orders},
                    [approvalType, , cartId],
                ) => ({
                    appliedCredits,
                    appliedPayments,
                    approvalType,
                    approverLevel,
                    shipping,
                    approvalGate    : "cart" as const,
                    id              : parseInt(cartId),
                    orders          : values(orders),
                })),
                ...cascadeMap(apiResponse, ["*", "orderLevelApproval", "*"], (order, [approvalType, , orderId]) => ({
                    approvalType,
                    ...mapLegacyOrderAsAppliedPayments(order),
                    approvalGate    : "order" as const,
                    approverLevel   : order.finalApprovalLevel,
                    id              : parseInt(orderId),
                    orders          : [order],
                })),
                ...cascadeMap(apiResponse, ["*", "proofLevelApproval", "*"], (order, [approvalType, , orderId]) => ({
                    approvalType,
                    approvalGate    : "proof" as const,
                    approverLevel   : order.finalApprovalLevel,
                    id              : parseInt(orderId),
                    orders          : [order],
                }))
            ];
        },
        next,
    }))
    .then("searchState", ({}, next:ComponentLink<typeof UseState<string>>) => UseState({next, initValue: ""}))
    .then("filteredApprovalRequests", value(
        ({approvalRequestsLoad: {value: approvalRequests}, searchState: {value: search}}) => (
            approvalRequests?.filter(makeApprovalFilter(search)) ?? []
        )
    ))

    .then("attributesService", ({approvalRequestsLoad: {reload}, noti, routerHistory}, next:ComponentLink<typeof ApprovalRequestAttributesService, "renderChildren">) => (
        ApprovalRequestAttributesService({
            // decorate attributes to react after approvals and denials
            approvalAttributeDecorator  : ({approval, attribute}) => {
                if (isApprovalAttributeOfKey(attribute, "approve") && approval.approvalGate !== "proof") {
                    return {
                        ...attribute,
                        key : "approve",
                        run : () => attribute.run().then(({cartId, submitToNextApprover, msg}) => {
                            if (!submitToNextApprover) {
                                if (approval.approvalGate === "cart") {
                                    routerHistory.push(`/checkout-success/${cartId}?referrer=approval-requests`);
                                } else {
                                    window.location.href = `/next/checkout_complete.php` + buildUrlQuery({
                                        id_cart     : cartId,
                                        id_order    : approval.id,
                                    });
                                }
                            } else {
                                noti.show({
                                    message: msg,
                                    color: "green",
                                    duration: 5000,
                                });
                                reload();
                            }

                            return {cartId, submitToNextApprover, msg};
                        }).catch(() => {
                            // if approval failed, reload
                            reload();
                            return Promise.reject();
                        }),
                    };
                } else if (isApprovalAttributeOfKey(attribute, "deny")) {
                    return {
                        ...attribute,
                        key : "deny",
                        run : () => attribute.run().then(reload as () => void),
                    };
                }
                return attribute;
            },
            orderAttributeDecorator     : ({attribute}) => {
                if (attribute.key === "deny") {
                    return {
                        ...attribute,
                        run: () => attribute.run().then(reload as () => void),
                    };
                }
                return attribute;
            },
            renderChildren              : next,
        })
    ))
    .then("approvalRequestAttributes", value(({attributesService, filteredApprovalRequests}) => (
        filteredApprovalRequests.map(attributesService.mapApprovalRequestAsAttributes)
    )))
    .then("attributeKeys", value(({approvalRequestAttributes}) => (
        approvalRequestAttributes.reduce((keys, attributes) => (
            keys.concat((Object.keys(attributes) as ApprovalRequestAttribute['key'][]).filter((key) => !keys.includes(key)))
        ), [] as ApprovalRequestAttribute['key'][])
    )))

    .then("sortState", ({}, next:ComponentLink<typeof StorageState<{ascending: boolean, name: string}>>) => StorageState({
        next,
        storage: window.localStorage,
        key: "approval-dashboard-sort-f0c4d1",
        defaultValue: {ascending: false, name: "date"},
    }))
    .then("sortModes", value(({attributeKeys}) => makeSortModes({attributeKeys})))

    .then("approvalRequestEntries", value(({
        approvalRequestAttributes,
        filteredApprovalRequests,
        sortModes,
        sortState,
    }) => {
        const activeSortMode    = sortModes.find(sortMode => sortMode.name === sortState.value.name);
        const entries           = filteredApprovalRequests.map((approval, index) => ({approval, attributes: approvalRequestAttributes[index]}));
        if (!activeSortMode) {
            return entries;
        }
        return sorted(entries, (entry) => activeSortMode?.getSortValue(entry, sortState.value.ascending), !sortState.value.ascending);
    }))

    .then("expandedState", ({}, next:ComponentLink<typeof UseState<number[]>>) => (
        UseState({next, initValue: []})
    ))

    .then(({approvalRequestsLoad: {loading, value: approvalRequests}, branchList, searchState, selectedBranch, showOfficeApprovals, sortModes, sortState: {onChange: setSort, value: activeSortMode}, userLevels}, next:Link<React.ReactNode>) => (
            <div className="approval-requests-11w">
                <div className="filters-ui">
                    {userLevels.userLevel === COMPANY_LEVEL && (
                        <div className="branch-dropdown">
                            {SearchableSelect({
                                allowClear: true,
                                getSublabel: (branch) => (<>
                                    <div
                                        title={[
                                            branch.branchAddress1,
                                            branch.branchAddress2,
                                            branch.branchCity,
                                            branch.branchState,
                                            branch.branchZip,
                                        ].filter(Boolean).join(", ")}
                                    >
                                        {[
                                            branch.branchAddress1,
                                            branch.branchAddress2,
                                            branch.branchCity,
                                            branch.branchState,
                                            branch.branchZip,
                                        ].filter(Boolean).join(", ")}
                                    </div>
                                    <div
                                        title={`${branch.branchLinkId} ${userLevels.internalUser && "| Internal ID: "} ${branch.idBranch}`}
                                        xd-qa-type="dropdown_item"
                                        xd-qa-name={`Office ${branch.idBranch}`}
                                    >
                                        {branch.branchLinkId} {userLevels.internalUser && `| Internal ID: ${branch.idBranch}`}
                                    </div>
                                </>),
                                list: chain(
                                    branchList ?? [],
                                    (_) => sortMulti(_, [
                                        (branch) => branch["branchName"]?.toLowerCase(),
                                    ]),
                                ),
                                loading: !branchList,
                                placeholder: "Select an office",
                                getLabel: (branch) => chain(
                                    branch.branchName || branch.branchLinkId,
                                    (label) => <span title={label} xd-qa-type="dropdown_item" xd-qa-name={`Office ${label}`}>{label}</span>
                                ),
                                mapSearchAsItemFilter: (search:string) => {
                                    const searchLowercase = search.toLowerCase();
                                    return (branch) => (branch.idBranch.includes(searchLowercase)) || [
                                        "branchName",
                                        "branchLinkId",
                                        "branchAddress1",
                                        "branchAddress2",
                                        "branchCity",
                                        "branchState",
                                    ].some(field => branch[field].toLowerCase().includes(searchLowercase))
                                },
                                onSelect: (branch) => (branch === null ?
                                    selectedBranch.onChange(null) :
                                    selectedBranch.onChange(branch.idBranch)
                                ),
                                isSelected: (branch) => selectedBranch.value === branch.idBranch,

                                className: "office",
                                searchLabel: "Search an office",
                                xdQaName: "Office",
                                scrollCloses: true,
                            })}
                        </div>
                    )}
                    {userLevels.userLevel === COMPANY_LEVEL && (
                        <a
                            className="slider-label"
                            onClick={() => showOfficeApprovals.onChange(!showOfficeApprovals.value ? 'true' : null)}
                            xd-qa-type="link"
                            xd-qa-name={showOfficeApprovals.value ? "Switch To Company-Level Approval Dashboard" : "Switch To Office-Level Approval Dashboard"}

                        >{showOfficeApprovals.value ? "Switch To Company-Level Approval Dashboard" : "Switch To Office-Level Approval Dashboard"}</a>
                    )}
                    <div className={cx({'search-input': true, 'active': searchState.value})}>
                        <input
                            onChange={(e) => searchState.onChange(e.target.value)}
                            value={searchState.value}

                            placeholder="Search"
                            title="Search by order ID, cart ID, user names and office name"
                            xd-qa-type="textbox"
                            xd-qa-name="Search Order"
                        />
                        <button
                            onClick={(e) => searchState.value ? searchState.onChange("") : (e.currentTarget.previousSibling as HTMLInputElement).focus()}
                            title={searchState.value ? "Clear Search" : "Search"}
                            xd-qa-type="button"
                            xd-qa-name={searchState.value ? "Clear Search" : "Search"}
                        >
                            <FontAwesomeIcon icon={searchState.value ? faCircleXmark : faSearch} xd-qa-type="icon" xd-qa-name="Search"/>
                        </button>
                    </div>
                    <ViewportTest if={MEDIA_QUERY_MD} then={() => (
                        <div className="sort-ui">
                            <Select2<ApprovalRequestSortMode>
                                list={sortModes}
                                getLabel={(sortMode) => `Sort by ${sortMode.label}`}
                                onSelect={({defaultAscending: ascending, name}) => setSort({ascending, name})}
                                isSelected={(sortMode) => sortMode.name === activeSortMode.name}
                                disableClearingSelection={true}
                                xdQaName="Sort Dropdown"
                            />
                            <button onClick={() => setSort({ascending: !activeSortMode.ascending, name: activeSortMode.name})} className="icon"
                                xd-qa-type="button"
                                xd-qa-name={"Sort Ascending " + activeSortMode.ascending}
                            ><FontAwesomeIcon icon={activeSortMode.ascending ? faArrowUp : faArrowDown} xd-qa-type="icon" xd-qa-name="Sort"/></button>
                        </div>
                    )}/>
                </div>
                {loading ? (
                    <div className="loading" xd-qa-type="label" xd-qa-name="Loading Message">
                        Loading approval requests...
                    </div>
                ) : approvalRequests.length === 0 ? (
                    <div className="no-data" xd-qa-type="label" xd-qa-name="No Requests Message">
                        {searchState.value ? "No results for your search." : "No approval requests available."}
                    </div>
                ) : <main>{next()}</main>}
            </div>
    ))
    .then(({
        approvalRequestEntries,
        attributeKeys,
        attributesService,
        expandedState: {onChange: setExpandedIds,
        value: expandedIds},
        sortModes,
        sortState,
    }) => <ViewportTest
        if={MEDIA_QUERY_MD}
        then={() => ApprovalRequestsCardListView({
            approvalRequestEntries,
            attributesService,
        })}
        else={() => ApprovalRequestsTableView({
            approvalRequestEntries,
            attributeKeys,
            attributesService,
            expandedIds,
            sortModes,
            sortState,
            onToggleAllExpanded : () => setExpandedIds(expandedIds.length > 0 ? [] : approvalRequestEntries.map(({approval}) => approval.id)),
            onToggleRowExpanded : (id) => setExpandedIds(expandedIds.includes(id) ? expandedIds.filter(otherId => id !== otherId) : [...expandedIds, id])
        })}
    />)
    .run()
);

const ClearEmulation = ({next}:{next: Link<JSX.Element>}) => cs(
    consumeContext("session"),
    ({session}) => <>
        {next()}

        {!session.loading && cs(
            consumeContext("apis"),
            ({}) => (
                Invoke({
                    fn: async () => {
                        if (session.emulated) {
                            await session.clearEmulation();
                        }
                    },
                })
            )
        )}
    </>,
);

const STRING_LOWEST  = String.fromCharCode(0);
const STRING_HIGHEST = String.fromCharCode(65535);

const makeSortModes = ({attributeKeys}:{
    attributeKeys: ApprovalRequestAttribute['key'][]
}): ApprovalRequestSortMode[] => (
    ([
        {
            defaultAscending    : true,
            name                : "alerts",
            label               : "Alerts",
            getSortValue        : ({attributes: {hasFastShipping, hasPromoCode}}) => (
                (hasPromoCode?.expires?.toISOString() ?? "9999-99-99") + ((SHIPPING_SERVICE_LEVELS_SORTED.indexOf(hasFastShipping?.level) + 1) || 99)
            ),
        },
        {
            defaultAscending    : true,
            name                : "id",
            label               : ATTRIBUTE_KEY_LABELS['id'],
            getSortValue        : ({approval}) => approval.id,
        },
        {
            defaultAscending    : true,
            name                : "branch",
            label               : ATTRIBUTE_KEY_LABELS['branch'],
            getSortValue        : ({attributes: {branch}}, ascending) => branch?.value.name ?? (ascending ? STRING_HIGHEST : STRING_LOWEST),
        },
        {
            defaultAscending    : true,
            name                : "agent",
            label               : ATTRIBUTE_KEY_LABELS['agent'],
            getSortValue        : ({attributes: {agent}}, ascending) => agent?.isBranchOrder ? "Branch Order" : (agent?.value?.name ?? (ascending ? STRING_HIGHEST : STRING_LOWEST)),
        },
        {
            defaultAscending    : true,
            name                : "agentEmail",
            label               : "User Email",
            getSortValue        : ({approval}, ascending) => approval.orders[0].usageEmail ?? (ascending ? STRING_HIGHEST : STRING_LOWEST),
        },
        {
            defaultAscending    : false,
            name                : "date",
            label               : ATTRIBUTE_KEY_LABELS['date'],
            getSortValue        : ({attributes: {date}}, ascending) => date?.value.toISOString() ?? (ascending ? STRING_HIGHEST : STRING_LOWEST),
        },
        {
            defaultAscending    : true,
            name                : "price",
            label               : ATTRIBUTE_KEY_LABELS['price'],
            getSortValue        : ({attributes: {price}}, ascending) => price?.value ?? (ascending ? Infinity : -Infinity),
        },
    ] as ReturnType<typeof makeSortModes>)
    .filter(sortMode =>
        sortMode.name === "id"
        || (
            sortMode.name === "alerts"
            && attributeKeys.includes("hasFastShipping") || attributeKeys.includes("hasPromoCode")
        )
        || (sortMode.name === "agentEmail" && attributeKeys.includes("agent"))
        || attributeKeys.includes(sortMode.name as ApprovalRequestAttribute['key'])
    )
);

const mapLegacyOrderAsAppliedPayments = (order:Types.Apis.Models.StandaloneOrderPendingApproval): (
    {
        appliedCredits  : Types.Apis.Models.AppliedCredits,
        appliedPayments : Types.Apis.Models.AppliedPayments,
    }
) => order.orderCorrupt ? null : ({
    appliedCredits                  : {
        adBudgets       : order.adBudgets?.map?.(adBudget => ({
            ...adBudget,
            isAmount    : true,
            itemId      : order.idOrder,
            meta1       : null,
            meta2       : null,
        })),
        copays          : (
            order.appliedPayments.coPay
            ? [{amount: order.appliedPayments.coPay, itemId: order.idOrder}]
            : []
        ),
        promoCodes      : (
            order.promoCode
            ? [{...order.promoCode, itemId: order.idOrder}]
            : []
        ),
        storeCredits    : (
            order.appliedPayments.storeCredit
            ? [{amount: order.appliedPayments.storeCredit, itemId: order.idOrder}]
            : []
        ),
        vouchers        : (
            order.appliedPayments.voucher
            ? [{amount: order.appliedPayments.voucher, name: "Voucher", itemId: order.idOrder}]
            : []
        ),
    },
    appliedPayments                 : {
        billing     : (
            order.appliedPayments.billing
            ? [{
                amount      : order.appliedPayments.billing,
                billingId   : "",
                comments    : order.billing?.comments ?? null,
                itemId      : order.idOrder,
                meta1       : null,
                meta2       : null,
                name        : order.billing?.name ?? "Billing",
            }]
            : []
        ),
        creditCards : (
            order.appliedPayments.cCTotal
            ? [{
                amount      : order.appliedPayments.cCTotal,
                cardToken   : null,
                type        : null,
            }]
            : []
        ),
    },
});

const makeApprovalFilter = (search:string) => {
    const searchLc = search.toLowerCase();
    return ({orders, orders: [order], id}:ApprovalRequest): boolean => [
        id.toString(),
        order.idAgent,
        order.idBranch,
        order.agentName,
        order.branchName,
        ...orders.map(({idOrder}) => idOrder?.toString())
    ].some(val => val?.toLowerCase().includes(searchLc));
};

